/**
 * UGC Review Wall — seamless auto-scroll review cards.
 *
 * Behaviour:
 *   • Idle state scrolls continuously and loops seamlessly (two identical
 *     card lists; the offset wraps by one list width, which is invisible).
 *   • Pointer enters the wall  → speed eases down to a smooth stop.
 *   • Pointer leaves the wall   → speed eases back up to the base speed.
 *   • Touch start/end mirrors the hover behaviour.
 *
 * The easing is a time-based exponential approach toward a target speed, so
 * it is frame-rate independent and needs no external animation library.
 *
 * Custom Element: <ugc-review-wall>
 * Data attributes:
 *   data-speed      — base speed setting (10-100), mapped to px/s
 *   data-direction  — "left" (default) or "right"
 */
if (!customElements.get('ugc-review-wall')) {
  customElements.define(
    'ugc-review-wall',
    class UgcReviewWall extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('.ugc-review-wall__track');
        if (!this.track) return;

        // direction: -1 moves content left, +1 moves content right.
        // NB: must NOT be named `this.dir` — that collides with the native
        // HTMLElement.dir (text-direction) property and reads back as "".
        this._dir = this.dataset.direction === 'right' ? 1 : -1;

        // Map speed setting (10-100) to px/s; default ~48px/s
        const raw = parseFloat(this.dataset.speed);
        this.baseSpeed = Number.isFinite(raw) ? raw * 1.2 : 48;

        this.offset = 0;
        this.listWidth = 0;
        this.needsMeasure = true;
        this.lastTime = 0;

        // current/target scroll speed (px/s)
        this.speed = this.baseSpeed;
        this.target = this.speed;

        // time constant (seconds) for the ease — larger = gentler ramp
        this.tau = 0.5;

        this._tick = this._tick.bind(this);
        // Hover/touch the wall → ease to a pause; leave → ease back to speed.
        this._onEnter = () => { this.target = 0; };
        this._onLeave = () => { this.target = this.baseSpeed; };
        this._onResize = () => { this.needsMeasure = true; };

        this.addEventListener('pointerenter', this._onEnter);
        this.addEventListener('pointerleave', this._onLeave);
        this.addEventListener('touchstart', this._onEnter, { passive: true });
        this.addEventListener('touchend', this._onLeave);
        this.addEventListener('touchcancel', this._onLeave);
        window.addEventListener('resize', this._onResize);

        if ('ResizeObserver' in window) {
          this._ro = new ResizeObserver(this._onResize);
          const first = this.track.querySelector('.ugc-review-wall__list');
          if (first) this._ro.observe(first);
        }

        this.raf = requestAnimationFrame(this._tick);
      }

      disconnectedCallback() {
        if (this.raf) cancelAnimationFrame(this.raf);
        this.removeEventListener('pointerenter', this._onEnter);
        this.removeEventListener('pointerleave', this._onLeave);
        this.removeEventListener('touchstart', this._onEnter);
        this.removeEventListener('touchend', this._onLeave);
        this.removeEventListener('touchcancel', this._onLeave);
        window.removeEventListener('resize', this._onResize);
        if (this._ro) this._ro.disconnect();
      }

      _measure() {
        const first = this.track.querySelector('.ugc-review-wall__list');
        if (!first) return;
        const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
        // One repeat = a full list plus the gap before the next (identical) list
        this.listWidth = first.getBoundingClientRect().width + gap;
        this.needsMeasure = false;
      }

      _tick(now) {
        this.raf = requestAnimationFrame(this._tick);

        if (!this.lastTime) this.lastTime = now;
        const dt = Math.min((now - this.lastTime) / 1000, 0.05); // cap to avoid jumps
        this.lastTime = now;

        if (this.needsMeasure) this._measure();

        // Ease current speed toward the target (frame-rate independent).
        const k = 1 - Math.exp(-dt / this.tau);
        this.speed += (this.target - this.speed) * k;
        if (Math.abs(this.speed - this.target) < 0.02) this.speed = this.target;

        // Advance and wrap into (-listWidth, 0]; the wrap is invisible
        // because the two card lists are identical.
        this.offset += this.speed * dt * this._dir;
        if (this.listWidth > 0) {
          while (this.offset <= -this.listWidth) this.offset += this.listWidth;
          while (this.offset > 0) this.offset -= this.listWidth;
        }

        this.track.style.transform = 'translate3d(' + this.offset.toFixed(2) + 'px,0,0)';
      }
    }
  );
}
